import { useEffect, useMemo, useState, useRef } from "react";
import {
  Plus,
  PanelLeft,
  Info,
  LogOut,
  Search,
  Folder,
  MessageSquare,
  Loader2,
  Home,
  Rocket,
  Bot,
  Server,
  Monitor,
  ExternalLink,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { store, useStore } from "@/lib/store";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface CommandItem {
  id: string;
  label: string;
  type:
    | "action"
    | "navigation"
    | "platform-project"
    | "platform-assistant"
    | "platform-server"
    | "session"
    | "search-result";
  icon: React.ReactNode;
  action: () => void;
  projectSlug?: string;
  snippet?: string;
}

// Fixed group headings per item type; sessions group by projectSlug instead
// (null), matching the original grouping behavior.
const GROUP_TITLES: Record<CommandItem["type"], string | null> = {
  action: "Actions",
  navigation: "Go to",
  "platform-project": "Projects",
  "platform-assistant": "Assistants",
  "platform-server": "MCP Servers",
  "search-result": "Search Results",
  session: null,
};

interface CommandGroup {
  title: string;
  items: CommandItem[];
}

// Rendered form of a group: each item keeps the index it occupies in the flat
// filtered list, which is what the arrow keys move through.
interface IndexedCommandGroup {
  title: string;
  items: { item: CommandItem; index: number }[];
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNewSession: () => void;
  onToggleSidebar: () => void;
  onOpenInfo: () => void;
  onLogout: () => void;
}

export function CommandPalette({
  open,
  onOpenChange,
  onNewSession,
  onToggleSidebar,
  onOpenInfo,
  onLogout,
}: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchResults, setSearchResults] = useState<Array<{ sessionId: string; slug: string; title: string; cwd: string; snippet: string; mtimeMs: number }>>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const state = useStore();

  const allItems = useMemo<CommandGroup[]>(() => {
    const actions: CommandItem[] = [
      {
        id: "new-session",
        label: "New Session",
        type: "action",
        icon: <Plus className="h-4 w-4" />,
        action: () => {
          onOpenChange(false);
          onNewSession();
        },
      },
      {
        id: "toggle-sidebar",
        label: "Toggle Sidebar",
        type: "action",
        icon: <PanelLeft className="h-4 w-4" />,
        action: () => {
          onOpenChange(false);
          onToggleSidebar();
        },
      },
      {
        id: "open-info",
        label: "Open Session Info",
        type: "action",
        icon: <Info className="h-4 w-4" />,
        action: () => {
          onOpenChange(false);
          onOpenInfo();
        },
      },
      {
        id: "logout",
        label: "Logout",
        type: "action",
        icon: <LogOut className="h-4 w-4" />,
        action: () => {
          onOpenChange(false);
          onLogout();
        },
      },
    ];

    const goTo = (view: Parameters<typeof store.navigate>[0]) => () => {
      onOpenChange(false);
      store.navigate(view);
    };

    const navigation: CommandItem[] = [
      {
        id: "nav-home",
        label: "Home",
        type: "navigation",
        icon: <Home className="h-4 w-4" />,
        action: goTo({ name: "home" }),
      },
      {
        id: "nav-projects",
        label: "Projects",
        type: "navigation",
        icon: <Rocket className="h-4 w-4" />,
        action: goTo({ name: "projects" }),
      },
      {
        id: "nav-assistants",
        label: "Assistants",
        type: "navigation",
        icon: <Bot className="h-4 w-4" />,
        action: goTo({ name: "assistants" }),
      },
      {
        id: "nav-servers",
        label: "MCP Servers",
        type: "navigation",
        icon: <Server className="h-4 w-4" />,
        action: goTo({ name: "servers" }),
      },
      {
        id: "nav-machines",
        label: "Machines",
        type: "navigation",
        icon: <Monitor className="h-4 w-4" />,
        action: goTo({ name: "machines" }),
      },
      {
        id: "open-dashboard",
        label: "Open opzero.sh dashboard",
        type: "navigation",
        icon: <ExternalLink className="h-4 w-4" />,
        action: () => {
          onOpenChange(false);
          window.open("https://opzero.sh/dashboard", "_blank");
        },
      },
    ];

    const platform = state.platform;
    const projectItems: CommandItem[] = platform.available && platform.projects.loaded
      ? platform.projects.items.map((p) => ({
          id: `platform-project-${p.id}`,
          label: `Project: ${p.name}`,
          type: "platform-project" as const,
          icon: <Rocket className="h-4 w-4" />,
          action: goTo({ name: "project", id: p.id }),
        }))
      : [];
    const assistantItems: CommandItem[] = platform.available && platform.assistants.loaded
      ? platform.assistants.items.map((a) => ({
          id: `platform-assistant-${a.id}`,
          label: `Assistant: ${a.name}`,
          type: "platform-assistant" as const,
          icon: <Bot className="h-4 w-4" />,
          action: goTo({ name: "assistant", id: a.id }),
        }))
      : [];
    const serverItems: CommandItem[] = platform.available && platform.mcpServers.loaded
      ? platform.mcpServers.items.map((s) => ({
          id: `platform-server-${s.id}`,
          label: `Server: ${s.name}`,
          type: "platform-server" as const,
          icon: <Server className="h-4 w-4" />,
          action: goTo({ name: "server", id: s.id }),
        }))
      : [];

    const sessionItems: CommandItem[] = [];
    for (const [slug, sessions] of Object.entries(state.sessionsByProject)) {
      for (const session of sessions) {
        sessionItems.push({
          id: `session-${session.id}`,
          label: session.title || session.id.slice(0, 12),
          type: "session",
          icon: <MessageSquare className="h-4 w-4" />,
          action: () => {
            store.openSession(slug, session.id);
            onOpenChange(false);
          },
          projectSlug: slug,
        });
      }
    }

    const searchResultItems: CommandItem[] = searchResults.map((r) => ({
      id: `search-${r.sessionId}`,
      label: r.title,
      type: "search-result" as const,
      icon: <MessageSquare className="h-4 w-4" />,
      action: () => {
        store.openSession(r.slug, r.sessionId);
        onOpenChange(false);
      },
      projectSlug: r.slug,
      snippet: r.snippet,
    }));

    const groups: CommandGroup[] = [];
    if (actions.length > 0) groups.push({ title: "Actions", items: actions });
    groups.push({ title: "Go to", items: navigation });
    if (projectItems.length > 0) groups.push({ title: "Projects", items: projectItems });
    if (assistantItems.length > 0) groups.push({ title: "Assistants", items: assistantItems });
    if (serverItems.length > 0) groups.push({ title: "MCP Servers", items: serverItems });
    if (searchResultItems.length > 0) groups.push({ title: "Search Results", items: searchResultItems });
    else if (sessionItems.length > 0) groups.push({ title: "Sessions", items: sessionItems });
    return groups;
  }, [state.sessionsByProject, state.platform, searchResults, onNewSession, onToggleSidebar, onOpenInfo, onLogout, onOpenChange]);

  const flatItems = useMemo(() => {
    return allItems.flatMap((g) => g.items);
  }, [allItems]);

  const filteredItems = useMemo(() => {
    if (!query.trim()) return flatItems;
    if (searchResults.length > 0) return flatItems;
    const q = query.toLowerCase();
    return flatItems.filter((item) => item.label.toLowerCase().includes(q));
  }, [flatItems, query, searchResults]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIndex(0);
      setSearchResults([]);
      setSearchLoading(false);
      if (searchTimer.current) clearTimeout(searchTimer.current);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await api.searchSessions(query);
        setSearchResults(data.results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [query]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => (i + 1) % filteredItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => (i - 1 + filteredItems.length) % filteredItems.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = filteredItems[selectedIndex];
      if (item) item.action();
    }
  }

  // Groups carry each item's index into filteredItems so the render loop never
  // has to search for it, and so hover/selection agree with the arrow keys
  // (which index into filteredItems, not the unfiltered list).
  const groupedFiltered = useMemo(() => {
    const groups: IndexedCommandGroup[] = [];
    let currentGroup: IndexedCommandGroup | null = null;
    for (let index = 0; index < filteredItems.length; index++) {
      const item = filteredItems[index];
      const title = GROUP_TITLES[item.type] ?? item.projectSlug ?? "unknown";
      if (!currentGroup || currentGroup.title !== title) {
        currentGroup = { title, items: [] };
        groups.push(currentGroup);
      }
      currentGroup.items.push({ item, index });
    }
    return groups;
  }, [filteredItems]);

  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex, filteredItems]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass glass-border p-0 max-w-md w-[90vw] overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border/40">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search sessions and actions..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <kbd className="text-[10px] text-muted-foreground bg-secondary/50 px-1.5 py-0.5 rounded">ESC</kbd>
        </div>
        <div className="max-h-[300px] overflow-y-auto py-1">
          {searchLoading ? (
            <div className="px-3 py-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Searching...
            </div>
          ) : groupedFiltered.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              No results found
            </div>
          ) : (
            groupedFiltered.map((group) => (
              <div key={group.title}>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
                  <Folder className="h-3 w-3" />
                  {group.title}
                </div>
                {group.items.map(({ item, index }) => {
                  const isSelected = index === selectedIndex;
                  return (
                    <button
                      key={item.id}
                      ref={(el) => {
                        itemRefs.current[index] = el;
                      }}
                      type="button"
                      onClick={() => item.action()}
                      onMouseEnter={() => setSelectedIndex(index)}
                      className={cn(
                        "w-full px-3 py-2 flex items-center gap-2 text-sm text-left transition-colors",
                        isSelected
                          ? "bg-primary/20 text-primary"
                          : "text-foreground hover:bg-secondary/50",
                      )}
                    >
                      <span className={cn("shrink-0 mt-0.5", isSelected ? "text-primary" : "text-muted-foreground")}>
                        {item.icon}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{item.label}</span>
                        {item.snippet && (
                          <span className={cn(
                            "block text-[11px] truncate",
                            isSelected ? "text-primary/70" : "text-muted-foreground"
                          )}>
                            {item.snippet}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="px-3 py-2 border-t border-border/40 flex items-center justify-between text-[10px] text-muted-foreground">
          {searchResults.length > 0 ? (
            <span>{searchResults.length} result{searchResults.length !== 1 ? "s" : ""}</span>
          ) : query.trim().length >= 2 ? (
            <span>Type to search session content</span>
          ) : (
            <span>
              <kbd className="bg-secondary/50 px-1 rounded">↑↓</kbd> navigate
            </span>
          )}
          <span>
            <kbd className="bg-secondary/50 px-1 rounded">↵</kbd> select
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}